// Types for the Pivot — สรุปการขาย page.
// Mirrors gb-data-platform-backend/docs/openapi/pivot.yaml exactly (schema names match 1:1).
// Served by the platform API at /v1/pivot/* (backend: src/controllers/pivot.controller.ts).

export type StoreGroupBy = "customer_type" | "customer_group" | "route" | "branch";

export type StoreSortKey =
  | "customer_code"
  | "customer_name"
  | "branch"
  | "route"
  | "customer_type"
  | "customer_group"
  | "visit_count"
  | "sold_qty"
  | "sold_percent"
  | "carryover_qty"
  | "waste_qty"
  | "waste_percent"
  | "sales_amount"
  | "sales_amount_incl_vat";

export type ProductSortKey =
  /** `product.sequence` — the product tab's default, ascending, unsequenced SKUs last. */
  | "sequence"
  | "product_code"
  | "product_name"
  | "store_count"
  | "visit_count"
  | "sold_qty"
  | "sold_percent"
  | "carryover_qty"
  | "waste_qty"
  | "waste_percent"
  | "sales_amount"
  | "sales_amount_incl_vat";

export type VisitSortKey =
  | "customer_name"
  | "customer_code"
  | "branch"
  | "route"
  | "customer_type"
  | "customer_group"
  | "planned_count"
  | "visited_count"
  | "missed_count"
  | "unplanned_visit_count"
  | "days_since_last_visit"
  | "sold_qty"
  | "sold_percent"
  | "carryover_qty"
  | "waste_qty"
  | "waste_percent"
  | "sales_amount"
  | "sales_amount_incl_vat";

export type SortDir = "asc" | "desc";

/** One of the visit response's `summary.gap_buckets[].min_days`, or 0 for no gap filter. */
export type GapMinDays = number;

export type DayStatus = "VISITED" | "VISITED_UNPLANNED" | "MISSED" | "NOT_PLANNED";

// ---- shared building blocks --------------------------------------------------

export interface Metrics {
  visit_count: number;
  sold_qty: number;
  /**
   * Both shares are over `sold_qty + waste_qty` — เหลือบนเชลล์ takes no part in them, so
   * counting a shelf never moves a percentage. `null` when that denominator is 0.
   *
   * An unrecorded `waste_qty` counts as 0 inside the share, which pins a store-day nobody
   * recorded at 100%. Read `summary.data_quality` before trusting one.
   */
  sold_percent: number | null;
  /** เสีย — expired goods taken back. `null` when nothing recorded it, never 0. */
  waste_qty: number | null;
  waste_percent: number | null;
  /** เหลือบนเชลล์ — stock still on the shelf. `null` when nothing recorded it, never 0. */
  carryover_qty: number | null;
  /** VAT-exclusive, THB. */
  sales_amount: number;
  /** Gross of 7% VAT. Never sum with sales_amount. */
  sales_amount_incl_vat: number;
}

/** How far the เสีย / เหลือบนเชลล์ record reaches in this scope. Drives the warning banner. */
export interface DataQuality {
  /** Store tabs count store-days; the product tab counts SKU rows. */
  unit: "store_day" | "row";
  total_count: number;
  waste_unknown_count: number;
  carryover_unknown_count: number;
}

export interface Capabilities {
  waste: boolean;
  carryover: boolean;
  planned_visits: boolean;
  visit_cycle: boolean;
  customer_group: boolean;
  product_group: boolean;
  product_cost: boolean;
  payment_type: boolean;
}

// ---- rollup / data source ----------------------------------------------------

/**
 * Which path answered a pivot request.
 *
 * `ROLLUP` is entirely precomputed, `HYBRID` is precomputed for the closed days and live
 * from `live_from` onward — both are normal and say nothing on screen. `LIVE` means nothing
 * precomputed was used: the numbers are still correct, the request was just slow.
 */
export type PivotDataSourceMode = "ROLLUP" | "HYBRID" | "LIVE";

/**
 * Why the rollup was not used. Non-null exactly when `mode === "LIVE"` — a `HYBRID`
 * response has no reason, and its `live_from` is not a problem to report.
 *
 * This is the stable code and the canonical field; `data_source.message` is English prose
 * meant for logs and fallbacks, so the page branches on the code for its Thai copy.
 */
export type PivotRollupReason =
  | "STALE"
  | "COVERAGE_GAP"
  | "COVERAGE_TOO_SHORT"
  | "WINDOW_IS_TODAY"
  | "DISABLED"
  | "TIMEZONE_MISMATCH"
  | "NO_BRANCHES_IN_SCOPE"
  | "UNAVAILABLE";

/**
 * What the backend did about the slowness. It never makes the caller wait on a rebuild —
 * the request is answered live and the rebuild runs in the background.
 *
 * `STARTED` only means *this request* asked for one; a cluster-wide lock may have deflected
 * it to a run that was already going. Only `/pivot/rollup/status` knows whether one is
 * actually running, which is why the page polls it rather than trusting this field.
 */
export type PivotRefreshState =
  | "STARTED"
  | "ALREADY_RUNNING"
  | "COOLING_DOWN"
  | "DISABLED"
  | "NOT_REQUESTED";

export interface PivotRollupRefresh {
  state: PivotRefreshState;
  /** Estimated run length for STARTED/ALREADY_RUNNING, exact seconds left for COOLING_DOWN. */
  retry_after_seconds: number | null;
}

export interface PivotDataSource {
  mode: PivotDataSourceMode;
  reason: PivotRollupReason | null;
  /** English, safe to render verbatim. The page prefers its own Thai copy keyed on `reason`. */
  message: string | null;
  /** First day answered live. Present on HYBRID too, where it is expected, not a fault. */
  live_from: string | null;
  refreshed_at: string | null;
  refresh: PivotRollupRefresh;
}

/** `GET /v1/pivot/rollup/status`. Cheap enough to poll: one lock scan and one indexed count. */
export interface PivotRollupStatus {
  refreshing: boolean;
  /**
   * The trailing window the rebuild covers (always 14 days), NOT the range the pivot was
   * asked for — documents mutate retroactively, so a correction posted today can move a
   * number two weeks back.
   */
  window: { from: string; to: string };
  branch_count: number;
  units_total: number;
  /** Progress fields are `null` — not 0 — whenever `refreshing` is false. */
  units_done: number | null;
  percent: number | null;
  estimated_seconds_remaining: number | null;
  is_stale: boolean;
  refreshed_at: string | null;
  stalest_branch: string | null;
  max_age_hours: number;
}

export interface GroupRef {
  key: string;
  label: string | null;
  color: string | null;
}

export interface StoreRow {
  customer_code: string;
  customer_name: string;
  branch_code: string;
  branch_name: string | null;
  route_code: string;
  route_name: string | null;
  customer_type_code: string | null;
  customer_type_name: string | null;
  customer_group_code: string | null;
  customer_group_name: string | null;
  is_in_master: boolean;
  district: string | null;
  province: string | null;
  metrics: Metrics;
}

export interface ProductRow {
  product_code: string;
  product_name: string | null;
  unit_price: number | null;
  unit_cost: number | null;
  shelf_life_days: number | null;
  /** `product.sequence`; null = unsequenced. Optional until the backend that sends it is deployed. */
  sequence?: number | null;
  is_vat_applicable: boolean | null;
  product_group_code: string | null;
  product_group_name: string | null;
  store_count: number;
  metrics: Metrics;
}

export interface VisitDayCell {
  date: string;
  status: DayStatus;
  arrived_at: string | null;
  document_count: number;
  sold_qty: number | null;
  sales_amount: number | null;
}

export interface VisitRow {
  customer_code: string;
  customer_name: string;
  branch_code: string;
  branch_name: string | null;
  route_code: string;
  route_name: string | null;
  customer_type_code: string | null;
  customer_type_name: string | null;
  customer_group_code: string | null;
  customer_group_name: string | null;
  is_in_master: boolean;
  visit_cycle_weekdays: number[];
  visit_cycle_per_week: number | null;
  visit_cycle_code: string | null;
  visit_cycle_label: string | null;
  planned_count: number;
  visited_count: number;
  missed_count: number;
  unplanned_visit_count: number;
  days_since_last_visit: number;
  days_since_last_visit_is_capped: boolean;
  max_daily_sales_amount: number;
  days: VisitDayCell[];
  metrics: Metrics;
}

export interface VisitAttendanceTotals {
  planned_count: number;
  visited_count: number;
  missed_count: number;
  unplanned_visit_count: number;
  /** Worst (largest) value in the set, not a sum. */
  days_since_last_visit: number;
  visited_by_date: Record<string, number>;
  missed_by_date: Record<string, number>;
}

export interface StoreGroup {
  group: GroupRef;
  is_flat: boolean;
  row_count: number;
  truncated_row_count: number;
  rows: StoreRow[];
  totals: Metrics;
}

export interface ProductGroup {
  group: GroupRef;
  is_flat: boolean;
  /** Always every row in the group, page or no page. */
  row_count: number;
  truncated_row_count: number;
  rows: ProductRow[];
  /** Also page-independent: page 2 reports the same totals as page 1. */
  totals: Metrics;
  /**
   * The three paging keys are **absent entirely** unless `rows_per_group` was sent — test
   * with `in`/`undefined`, not truthiness, since `page` is 1-based but `page_count` can be 0.
   */
  page?: number;
  page_size?: number;
  page_count?: number;
}

export interface VisitGroup {
  group: GroupRef;
  row_count: number;
  truncated_row_count: number;
  rows: VisitRow[];
  totals: Metrics & VisitAttendanceTotals;
}

export interface Scope {
  date_from: string;
  date_to: string;
  timezone: string;
  span_days: number;
  currency: string;
  price_basis: "exclude_vat";
  vat_rate: number;
  branch_code: string | null;
  branch_locked: boolean;
  route_code: string | null;
  customer_type: string[];
  customer_group: string[];
  customer_code: string | null;
  /** Product filter echo — sent by the product-drill backend, absent on older ones. */
  product_code?: string[];
  q: string | null;
  group_by: string | null;
  sort_by: string;
  sort_dir: SortDir;
}

export interface TopRow {
  key: string | null;
  label: string | null;
  metrics: Metrics;
}

export interface GapBucket {
  min_days: number;
  store_count: number;
}

export interface GapTopRow {
  customer_code: string;
  customer_name: string;
  route_name: string | null;
  customer_type_name: string | null;
  days_since_last_visit: number;
  days_since_last_visit_is_capped: boolean;
  visited_count: number;
  planned_count: number;
}

export interface BaseSummary {
  row_count: number;
  group_count: number;
  truncated_group_count: number;
  data_quality: DataQuality;
}

// ---- endpoint responses -------------------------------------------------------

export interface StorePivotResponse {
  scope: Scope;
  capabilities: Capabilities;
  /** Optional only so a pre-rollup API does not break the page; always sent by the
   *  rollup backend, feature flag on or off. */
  data_source?: PivotDataSource;
  summary: BaseSummary & {
    grand_total: Metrics;
    skipped_row_count: number;
    top_rows: TopRow[];
  };
  groups: StoreGroup[];
}

export interface ProductPivotResponse {
  scope: Scope;
  capabilities: Capabilities;
  /** Optional only so a pre-rollup API does not break the page; always sent by the
   *  rollup backend, feature flag on or off. */
  data_source?: PivotDataSource;
  summary: BaseSummary & {
    grand_total: Metrics;
    top_rows: TopRow[];
  };
  groups: ProductGroup[];
}

/**
 * `GET /pivot/products/stores` — the stores that sold ONE product, grouped by channel
 * (`customer_type_code`, `__NONE__` for unclassified). Same envelope as the store view so the
 * rows reuse `StoreRow`; their metrics are that product's metrics at that store.
 *
 * Only stores with `sold_qty > 0` are listed, so `summary.row_count` equals the product
 * view's `store_count` for the same filters.
 */
export interface ProductStoresResponse {
  scope: Scope;
  product: { product_code: string; product_name: string | null };
  capabilities: Capabilities;
  data_source?: PivotDataSource;
  /**
   * The store summary shape (`top_rows` is always empty). ขาย / ยอดขาย / row_count reconcile
   * with the product row; ของเสีย / เหลือบนเชลล์ do NOT — the product row also counts stores
   * that sold none — so never sum-check those against it.
   */
  summary: BaseSummary & {
    grand_total: Metrics;
    top_rows: TopRow[];
    skipped_row_count: number;
  };
  groups: StoreGroup[];
}

/**
 * `GET /pivot/visit-times` — per store, the PWA visit-time medians over the selected days
 * (board A3, the extra columns on the สายส่ง tab). Stores with no PWA visit are absent.
 */
export interface VisitTimeRow {
  customer_code: string;
  route_code: string;
  visit_day_count: number;
  /** Local `HH:mm` — median / 25th / 75th percentile of the check-in time of day. */
  arrived_median: string | null;
  arrived_p25: string | null;
  arrived_p75: string | null;
  cycle_median_min: number | null;
  app_median_min: number | null;
  /** Visits carrying a GAP / APP flag. */
  flag_count: number;
}

export interface VisitTimesResponse {
  rows: VisitTimeRow[];
}

// ---- route-day times (PWA only) — tab "เวลาวิ่งสาย" -----------------------------

/** Straight-line distance band between consecutive check-ins, in metres. */
export type RouteDayBucket = "<30" | "30-200" | "200-1000" | "1000-5000" | "5000+";

export const ROUTE_DAY_BUCKETS: RouteDayBucket[] = ["<30", "30-200", "200-1000", "1000-5000", "5000+"];

/** Gap thresholds per (branch, bucket): p50 = "normal", p95 = "flag above". */
export interface RouteDayThreshold {
  branch_code: string;
  bucket: RouteDayBucket;
  p50: number;
  p95: number;
  sample_count: number;
  /** Too few samples in the branch — the company-wide table was used. */
  is_fallback: boolean;
}

export interface RouteDaySummary {
  route_day_count: number;
  shop_count: number;
  shops_median: number | null;
  cycle_median_min: number | null;
  gap_flag_count: number;
  flagged_route_day_count: number;
  /** Share of visits whose app time was under 0.5 min — checked in after the work. */
  checkin_after_work_pct: number | null;
}

/** One route on one stock date. Timestamps are ISO strings (UTC). */
export interface RouteDayRow {
  route_code: string;
  route_name: string | null;
  /** `YYYY-MM-DD`, the route_stock date. */
  date: string;
  leave_at: string | null;
  /** The branch checkout was only pressed on arrival at shop 1. */
  leave_at_first_shop: boolean;
  first_check_in: string | null;
  last_check_in: string | null;
  last_left_at: string | null;
  /** `reconcile.started_at` — the count start, NOT the van's return to the branch. */
  count_started_at: string | null;
  shops_visited: number;
  shops_planned: number | null;
  cycle_median_min: number | null;
  distance_km: number | null;
  app_median_min: number | null;
  gap_flag_count: number;
  app_flag_count: number;
  excess_min: number | null;
}

export interface RouteDayBranchGroup {
  branch_code: string;
  branch_name: string | null;
  rows: RouteDayRow[];
}

export interface RouteDaysResponse {
  scope: {
    date_from: string;
    date_to: string;
    timezone: string;
    branch_code: string | null;
    route_code: string | null;
  };
  thresholds: RouteDayThreshold[];
  summary: RouteDaySummary;
  groups: RouteDayBranchGroup[];
}

/** Plan order (stop_no) is not measured for now, so there is no ORDER tag. */
export type RouteDayStopTag = "GAP" | "APP" | "LUNCH" | "NO_SALE" | "NO_GPS";

export interface RouteDayStop {
  /** Actual order of the visit (1-based). */
  seq: number;
  customer_code: string;
  customer_name: string;
  check_in_at: string;
  bill_at: string | null;
  left_at: string | null;
  app_min: number | null;
  gap_min: number | null;
  /** The first stop: the gap is measured from the branch checkout and never flagged. */
  gap_from_branch: boolean;
  distance_m: number | null;
  bucket: RouteDayBucket | null;
  normal_min: number | null;
  threshold_min: number | null;
  tags: RouteDayStopTag[];
  /** Ex-VAT, same basis as the rest of Pivot. */
  sales_amount: number;
}

export interface RouteDayDetailResponse {
  header: RouteDayRow & { branch_code: string; branch_name: string | null };
  stops: RouteDayStop[];
}

export interface PivotRouteDaysParams {
  date_from: string;
  date_to: string;
  timezone?: string;
  branch_code?: string;
  route_code?: string;
  only_flagged?: boolean;
}

export interface PivotRouteDayDetailParams {
  route_code: string;
  date: string;
  timezone?: string;
}

export interface VisitGrid {
  date_from: string;
  date_to: string;
  day_count: number;
  is_truncated: boolean;
  max_days: number;
}

export interface VisitPivotResponse {
  scope: Scope;
  capabilities: Capabilities;
  /** Optional only so a pre-rollup API does not break the page; always sent by the
   *  rollup backend, feature flag on or off. */
  data_source?: PivotDataSource;
  grid: VisitGrid;
  summary: BaseSummary &
    VisitAttendanceTotals & {
      grand_total: Metrics & VisitAttendanceTotals;
      scope_store_count: number;
      gap_buckets: GapBucket[];
      gap_top_rows: GapTopRow[];
    };
  groups: VisitGroup[];
}

export interface CustomerOptionRoute {
  route_code: string;
  route_name: string | null;
  customers: {
    customer_code: string;
    customer_name: string;
    customer_type_code: string | null;
    customer_group_code: string | null;
  }[];
}

export interface CustomerOptionsResponse {
  is_truncated: boolean;
  total_count: number;
  routes: CustomerOptionRoute[];
}

// ---- request params -------------------------------------------------------

export interface PivotScopeParams {
  date_from: string;
  date_to: string;
  timezone?: string;
  branch_code?: string;
  route_code?: string;
  customer_type?: string[];
  customer_group?: string[];
}

export interface PivotStoresParams extends PivotScopeParams {
  group_by?: StoreGroupBy;
  q?: string;
  sort_by?: StoreSortKey;
  sort_dir?: SortDir;
  rows_per_group?: number;
  group_limit?: number;
}

export interface PivotProductsParams extends PivotScopeParams {
  customer_code?: string;
  /** Multi-value, repeated key like `customer_type`. Empty = every product. Max 200. */
  product_code?: string[];
  q?: string;
  sort_by?: ProductSortKey;
  sort_dir?: SortDir;
  /**
   * Opt in to paging. Absent — which is what this page sends — returns every SKU in each
   * group; a product group cannot exceed the catalogue, so there is nothing for a cap to
   * protect. Paging does not make the request faster either: the cost is the distinct
   * counts over the fact rows, not the rows returned.
   */
  rows_per_group?: number;
  /** 1-based, per group. Ignored without `rows_per_group`. */
  page?: number;
}

export interface PivotProductStoresParams extends PivotScopeParams {
  customer_code?: string;
  /** Required and single — the one product being opened. */
  product_code: string;
  q?: string;
  sort_by?: StoreSortKey;
  sort_dir?: SortDir;
  rows_per_group?: number;
  group_limit?: number;
}

export interface PivotVisitsParams extends PivotScopeParams {
  group_by?: StoreGroupBy;
  gap_min_days?: GapMinDays;
  grid_max_days?: number;
  q?: string;
  sort_by?: VisitSortKey;
  sort_dir?: SortDir;
  rows_per_group?: number;
  group_limit?: number;
}

export interface PivotCustomerOptionsParams {
  branch_code?: string;
  route_code?: string;
  customer_type?: string[];
  customer_group?: string[];
  q?: string;
  limit?: number;
}

export interface PivotRollupStatusParams {
  /** Branch-locked users are scoped from their token; sending it is only a narrowing. */
  branch_code?: string;
}

export type PivotExportView = "stores" | "products" | "visits" | "product_stores";

export interface PivotExportParams extends PivotScopeParams {
  view: PivotExportView;
  group_by?: StoreGroupBy;
  customer_code?: string;
  /** `products`: the multi filter. `product_stores`: exactly one code. */
  product_code?: string[];
  gap_min_days?: GapMinDays;
  q?: string;
  sort_by?: string;
  sort_dir?: SortDir;
}

// ---- page-local ----------------------------------------------------------

/** The page's five tabs. `ctype`/`route`/`branch` all back onto /pivot/stores. */
export type PivotTab = "ctype" | "route" | "branch" | "visit" | "sku" | "routetime";

export const PIVOT_TABS: PivotTab[] = ["ctype", "route", "branch", "visit", "sku", "routetime"];

/** The three tabs backed by /pivot/stores. */
export type PivotStoreTab = "ctype" | "route" | "branch";

export const PIVOT_TAB_GROUP_BY: Record<PivotStoreTab, StoreGroupBy> = {
  ctype: "customer_type",
  route: "route",
  branch: "branch",
};

export const isPivotStoreTab = (tab: PivotTab): tab is PivotStoreTab => tab in PIVOT_TAB_GROUP_BY;

/** The store view's `group_by` for a tab; `customer_type` for tabs that are not store tabs. */
export const storeGroupByForTab = (tab: PivotTab): StoreGroupBy =>
    isPivotStoreTab(tab) ? PIVOT_TAB_GROUP_BY[tab] : "customer_type";

export const GAP_LOOKBACK_CAP = 200;
